import { lazy, type ComponentProps } from 'react';
import { redirect, useLocation, type LoaderFunctionArgs, type RouteObject } from 'react-router';
import { object as zodObject, boolean as zodBoolean, type infer as zodInfer } from 'zod/mini';

import { LogoDestination } from '../../../assets/Logo/Logo';
import { ErrorTitle, ErrorMessage } from '../../../components/ErrorPage/ErrorPage.styled';
import { InitializationError } from '../../../components/ErrorPage/Errors';
import { NotFound } from '../../../components/ErrorPage/NotFound/NotFound';
import { ErrorBoundary } from '../../../ErrorBoundary';
import { AllowedTabsModel } from '../../../pages/PatientPortraitPage/AllowedTabsModel';
import {
	getNavinaPatientIdOrThrow,
	getOptionalNavinaAppointmentIdOrThrow,
	getSkipValidationSearchParam,
} from '../../../pages/PatientPortraitPage/patientPortraitParams.utils';
import { PATIENT_PORTRAIT_TABS_DATA } from '../../../pages/PatientPortraitPage/PatientPortraitTabsStaticData';
import { getAllowedTabsByPatientIdOnce } from '../../../server/PatientPortraitApi/ProductContextApi';
import { MainLayout, type LayoutHeaderProps } from '../../layouts/MainLayout/MainLayout';
import { ApplicationsMenuSelectedPage } from '../../layouts/MainLayout/MainLayoutHeader';
import { withLayout } from '../../layouts/withLayout';
import {
	PATIENT_AND_APPOINTMENT_CONTEXT_SUMMARY_PROPS,
	PATIENT_CONTEXT_SUMMARY_PROPS,
	LazyPatientPortraitPage,
	LazyPatientPortraitPageWithAppointment,
	LazyTabPageGuard,
} from './PatientPortraitRoutesCore';

const LazyPatientPortraitTabsLayoutPage = lazy(() =>
	import('../../../pages/PatientPortraitPage/PatientPortraitTabsLayoutPage').then((module) => ({
		default: module.PatientPortraitTabsLayoutPage,
	})),
);

const LazyPatientPortraitHeader = lazy(() =>
	import('../../../pages/PatientPortraitPage/PatientPortraitHeader').then((module) => ({
		default: module.PatientPortraitHeader,
	})),
);

const PATIENT_PORTRAIT_MAIN_LAYOUT_PROPS = {
	headerAppMenuSelectedPage: ApplicationsMenuSelectedPage.PatientPortrait,
	mainHeaderContent: <LazyPatientPortraitHeader />,
	headerLogoLink: LogoDestination.Schedule,
} as const satisfies LayoutHeaderProps;

const ERROR_ELEMENT_ROUTE_PROPS = {
	errorElement: <ErrorBoundary />,
} as const satisfies Partial<RouteObject>;

const PATIENT_PORTRAIT_TABS_LAYOUT = <LazyPatientPortraitTabsLayoutPage />;
const PATIENT_PORTRAIT_MAIN_LAYOUT = <MainLayout {...PATIENT_PORTRAIT_MAIN_LAYOUT_PROPS} />;

const EMBEDDED_PATIENT_PORTRAIT_TABS_LAYOUT = <LazyPatientPortraitTabsLayoutPage {...{ sideMenuComponent: null }} />;
const EMBEDDED_PATIENT_PORTRAIT_MAIN_LAYOUT = (
	<MainLayout
		{...({
			...PATIENT_PORTRAIT_MAIN_LAYOUT_PROPS,
			applicationsMenu: null,
		} as const satisfies LayoutHeaderProps)}
	/>
);

function NotFoundPageTabRoute(props: Readonly<ComponentProps<typeof NotFound>>) {
	const { pathname } = useLocation();
	const pathnameSegments = pathname.split('/').filter(Boolean);
	const lastSegment = pathnameSegments.at(-1);

	return (
		<NotFound
			{...{
				...props,
				entityName: `'${lastSegment}'`,
			}}
		/>
	);
}

export const PATIENT_PORTRAIT_ROUTE_HANDLE_SCHEMA = zodObject({
	showSideMenu: zodBoolean(),
});

export type PatientPortraitRouteHandle = zodInfer<typeof PATIENT_PORTRAIT_ROUTE_HANDLE_SCHEMA>;

// Every route rendered within the patient portrait tabs layout must declare a handle so the layout
// can resolve `showSideMenu` without falling back to a silent default. Routes that don't express a
// specific preference (the catch-all not-found route, the auto-tab-selector) use this explicit one.
const DEFAULT_PATIENT_PORTRAIT_ROUTE_HANDLE = PATIENT_PORTRAIT_ROUTE_HANDLE_SCHEMA.parse({
	showSideMenu: true,
});

const PATIENT_PORTRAIT_TABS = [
	...PATIENT_PORTRAIT_TABS_DATA.map(({ path, element, tab, showSideMenu }) => ({
		path,
		handle: PATIENT_PORTRAIT_ROUTE_HANDLE_SCHEMA.parse({
			showSideMenu,
		}),
		element: <LazyTabPageGuard {...{ currentTab: tab }} />,
		children: [
			{
				index: true,
				element,
			},
		],
	})),
	{
		path: '*',
		handle: DEFAULT_PATIENT_PORTRAIT_ROUTE_HANDLE,
		element: <NotFoundPageTabRoute />,
	},
] as const satisfies ReadonlyArray<RouteObject>;

// https://app.navina.ai/embedded/clinical-summary/patient/25b89318-f8ac-4250-8ce3-0c6c2d235394
function getEmbeddedTabOnlyPatientRouteObject({
	path,
	element,
	tab,
}: (typeof PATIENT_PORTRAIT_TABS_DATA)[number]): RouteObject {
	return {
		...PATIENT_CONTEXT_SUMMARY_PROPS,
		...ERROR_ELEMENT_ROUTE_PROPS,
		path: `/embedded/${path}/patient/:navinaPatientId`,
		element: <LazyPatientPortraitPage />,
		children: [
			{
				element: <LazyTabPageGuard {...{ currentTab: tab }} />,
				children: [{ index: true, element }],
			},
		],
	} as const satisfies RouteObject;
}

// https://app.navina.ai/embedded/clinical-summary/patient/25b89318-f8ac-4250-8ce3-0c6c2d235394/appointment/00d58f36-3f13-4efb-9466-7f424a09283f
function getEmbeddedTabOnlyPatientWithAppointmentRouteObject({
	path,
	element,
	tab,
}: (typeof PATIENT_PORTRAIT_TABS_DATA)[number]): RouteObject {
	return {
		...PATIENT_AND_APPOINTMENT_CONTEXT_SUMMARY_PROPS,
		...ERROR_ELEMENT_ROUTE_PROPS,
		path: `/embedded/${path}/patient/:navinaPatientId/appointment/:navinaAppointmentId`,
		element: <LazyPatientPortraitPageWithAppointment />,
		children: [
			{
				element: <LazyTabPageGuard {...{ currentTab: tab }} />,
				children: [{ index: true, element }],
			},
		],
	} as const satisfies RouteObject;
}

// https://app.navina.ai/embedded/patient/25b89318-f8ac-4250-8ce3-0c6c2d235394/clinical-summary
function getEmbeddedPatientRouteObject(): RouteObject {
	return {
		...ERROR_ELEMENT_ROUTE_PROPS,
		...PATIENT_CONTEXT_SUMMARY_PROPS,
		element: <LazyPatientPortraitPage />,
		children: [...EMBEDDED_PATIENT_PORTRAIT_TABS_WITH_AUTO_SELECT],
	} as const satisfies RouteObject;
}

// https://app.navina.ai/embedded/patient/25b89318-f8ac-4250-8ce3-0c6c2d235394/appointment/00d58f36-3f13-4efb-9466-7f424a09283f/clinical-summary
function getEmbeddedPatientWithAppointmentRouteObject(): RouteObject {
	return {
		...ERROR_ELEMENT_ROUTE_PROPS,
		...PATIENT_AND_APPOINTMENT_CONTEXT_SUMMARY_PROPS,
		element: <LazyPatientPortraitPageWithAppointment />,
		path: 'appointment/:navinaAppointmentId',
		children: [...EMBEDDED_PATIENT_PORTRAIT_TABS_WITH_AUTO_SELECT],
	} as const satisfies RouteObject;
}

// https://app.navina.ai/patient/25b89318-f8ac-4250-8ce3-0c6c2d235394/clinical-summary
function getPatientRouteObject(): RouteObject {
	return {
		...ERROR_ELEMENT_ROUTE_PROPS,
		...PATIENT_CONTEXT_SUMMARY_PROPS,
		element: <LazyPatientPortraitPage />,
		children: [...PATIENT_PORTRAIT_TABS_WITH_AUTO_SELECT],
	} as const satisfies RouteObject;
}

// https://app.navina.ai/patient/25b89318-f8ac-4250-8ce3-0c6c2d235394/appointment/00d58f36-3f13-4efb-9466-7f424a09283f/clinical-summary
function getPatientWithAppointmentRouteObject(): RouteObject {
	return {
		...ERROR_ELEMENT_ROUTE_PROPS,
		...PATIENT_AND_APPOINTMENT_CONTEXT_SUMMARY_PROPS,
		element: <LazyPatientPortraitPageWithAppointment />,
		path: 'appointment/:navinaAppointmentId',
		children: [...PATIENT_PORTRAIT_TABS_WITH_AUTO_SELECT],
	} as const satisfies RouteObject;
}

const EMBEDDED_TAB_ONLY_ROUTES = [
	...PATIENT_PORTRAIT_TABS_DATA.map(getEmbeddedTabOnlyPatientRouteObject),
	...PATIENT_PORTRAIT_TABS_DATA.map(getEmbeddedTabOnlyPatientWithAppointmentRouteObject),
] as const satisfies ReadonlyArray<RouteObject>;

const PATIENT_PORTRAIT_AUTO_TAB_SELECTOR = {
	element: PATIENT_PORTRAIT_TABS_LAYOUT,
	handle: DEFAULT_PATIENT_PORTRAIT_ROUTE_HANDLE,
	index: true,
	loader: async function autoSelectPatientPortraitTabLoader({ params, request }: LoaderFunctionArgs) {
		const { navinaPatientId: maybeNavinaPatientId, navinaAppointmentId: maybeNavinaAppointmentId } = params;

		const navinaPatientId = getNavinaPatientIdOrThrow(maybeNavinaPatientId);
		const navinaAppointmentId = getOptionalNavinaAppointmentIdOrThrow(maybeNavinaAppointmentId);
		const maybeSkipValidation = getSkipValidationSearchParam(new URL(request.url).searchParams);

		let allowedTabs: ReadonlyArray<string>;

		try {
			allowedTabs = await AllowedTabsModel.fetchAllowedTabs({
				patientId: navinaPatientId,
				maybeAppointmentId: navinaAppointmentId,
				maybeSkipValidation,
				getAllowedTabs: getAllowedTabsByPatientIdOnce,
			});
		} catch {
			throw new InitializationError('Unable to load available tabs for this patient. Please try again later.');
		}

		const [firstAllowedTab] = allowedTabs;

		if (!firstAllowedTab) {
			throw new Error('No allowed tabs configured for the user');
		}

		const newPath = PATIENT_PORTRAIT_TABS_DATA.find(function findDefaultTab({ featureFlagName }) {
			return featureFlagName === firstAllowedTab;
		});

		if (!newPath) {
			throw new Error('No matching tab found for the default page');
		}

		return redirect(newPath.path);
	},
} as const satisfies RouteObject;

const PATIENT_PORTRAIT_TABS_WITH_AUTO_SELECT = [
	PATIENT_PORTRAIT_AUTO_TAB_SELECTOR,
	...PATIENT_PORTRAIT_TABS,
] as const satisfies ReadonlyArray<RouteObject>;

const EMBEDDED_PATIENT_PORTRAIT_TABS_WITH_AUTO_SELECT = [
	{
		...PATIENT_PORTRAIT_AUTO_TAB_SELECTOR,
		element: EMBEDDED_PATIENT_PORTRAIT_TABS_LAYOUT,
	},
	...PATIENT_PORTRAIT_TABS,
] as const satisfies ReadonlyArray<RouteObject>;

const EMBEDDED_PATIENT_PORTRAIT_ROUTE = {
	...withLayout(EMBEDDED_PATIENT_PORTRAIT_MAIN_LAYOUT, [
		{
			path: '/embedded/patient/:navinaPatientId',
			children: [
				{
					element: EMBEDDED_PATIENT_PORTRAIT_TABS_LAYOUT,
					children: [getEmbeddedPatientWithAppointmentRouteObject(), getEmbeddedPatientRouteObject()],
				},
				...PATIENT_PORTRAIT_TABS,
			],
		},

		...EMBEDDED_TAB_ONLY_ROUTES,
	]),
} as const satisfies RouteObject;

const PATIENT_PORTRAIT_ROUTE = {
	...withLayout(PATIENT_PORTRAIT_MAIN_LAYOUT, [
		{
			element: PATIENT_PORTRAIT_TABS_LAYOUT,
			path: '/patient/:navinaPatientId',
			children: [getPatientWithAppointmentRouteObject(), getPatientRouteObject()],
		},
	]),
} as const satisfies RouteObject;

export const PATIENT_PORTRAIT_ROUTES = [
	{
		...PATIENT_CONTEXT_SUMMARY_PROPS,
		path: '/patient',
		element: (
			<NotFound
				{...{
					children: (
						<>
							<ErrorTitle>We’re missing a patient ID</ErrorTitle>
							<ErrorMessage>
								This page needs a patient ID in the URL (e.g., <code>{`/patient/{id}`}</code>). Without it, we can’t
								display the patient record.
							</ErrorMessage>
						</>
					),
				}}
			/>
		),
	},
	PATIENT_PORTRAIT_ROUTE,
	EMBEDDED_PATIENT_PORTRAIT_ROUTE,
] as const satisfies ReadonlyArray<RouteObject>;
